import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall, ENDPOINTS } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { FiFile, FiLock, FiUnlock, FiEdit, FiPrinter, FiDownload, FiShare2, FiPlus, FiUpload } from 'react-icons/fi';
import DocumentEditor from '../../DocumentEditor';

interface Document {
  id: string;
  title: string;
  document_date: string;
  direction?: string;
  document_type?: string;
  dictation_reference?: string;
}

interface DocumentContent {
  content: string;
  format: string;
}

interface DocumentsTabProps {
  patient: any;
  documents: any[];
}

interface DocumentItemProps {
  document: Document;
  isSelected: boolean;
  onClick: () => void;
  onEdit: () => void;
  onToggleLock: () => void;
  onDownload: () => void;
  onPrint: () => void;
  isLocked: boolean;
}

const DocumentItem: React.FC<DocumentItemProps> = ({ 
  document, 
  isSelected, 
  onClick, 
  onEdit,
  onToggleLock,
  onDownload,
  onPrint,
  isLocked
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`flex items-start space-x-2 p-3 hover:bg-base-200 cursor-pointer ${
        isSelected ? 'bg-primary/10' : ''
      }`}
      onClick={onClick}
    >
      <FiFile className="mt-1 opacity-50" />
      <div className="flex-1">
        <div className="text-sm font-medium">{document.title}</div>
        <div className="text-xs opacity-60">
          {formatDate(document.document_date)}
        </div>
        {document.dictation_reference && (
          <div className="text-xs opacity-40 mt-1">
            Generated from Dictation: {document.dictation_reference}
          </div>
        )}
        <div className="flex space-x-2 mt-1">
          <button
            className="p-1 opacity-50 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onToggleLock();
            }}
            title={isLocked ? 'Unlock document' : 'Lock document'}
          >
            {isLocked ? (
              <FiLock size={14} className="text-error" />
            ) : (
              <FiUnlock size={14} className="text-success" />
            )}
          </button>
          <button
            className={`p-1 ${
              isLocked 
                ? 'opacity-30 cursor-not-allowed' 
                : 'opacity-50 hover:opacity-100'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isLocked) onEdit();
            }}
            disabled={isLocked}
            title={isLocked ? 'Document is locked' : 'Edit document'}
          >
            <FiEdit size={14} />
          </button>
          <button
            className="p-1 opacity-50 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onPrint();
            }}
            title="Print document"
          >
            <FiPrinter size={14} />
          </button>
          <button
            className="p-1 opacity-50 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            title="Download document"
          >
            <FiDownload size={14} />
          </button>
          <button
            className="p-1 opacity-50 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              console.log('Share', document.id);
            }}
            title="Share document"
          >
            <FiShare2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const DocumentsTab: React.FC<DocumentsTabProps> = ({ patient, documents = [] }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const editorRef = React.useRef<any>(null);
  
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentContent, setDocumentContent] = useState<any>(null);
  const [documentContentFormat, setDocumentContentFormat] = useState<'sfdt' | 'text' | 'html' | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [documentStates, setDocumentStates] = useState<Record<string, {
    isEditing: boolean;
    isLocked: boolean;
  }>>({});

  console.log('📋 DocumentsTab rendered with patient:', patient?.id, 'documents:', documents?.length);

  // Initialize document states when documents change
  useEffect(() => {
    if (documents && documents.length > 0) {
      const states: Record<string, { isEditing: boolean; isLocked: boolean }> = {};
      documents.forEach((doc: Document) => {
        states[doc.id] = { isEditing: false, isLocked: true };
      });
      setDocumentStates(states);
    }
  }, [documents]);

  const loadDocumentContent = async (document: Document) => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiCall(
        `${ENDPOINTS.DOCUMENTS.CONTENT}${document.id}/content/`,
        { method: 'GET' },
        token
      );
      
      if (!response.ok) {
        throw new Error('Failed to load document content');
      }
      
      const contentData: DocumentContent = await response.json();
      
      setDocumentContent(contentData.content || '');
      setDocumentContentFormat((contentData.format as 'sfdt' | 'text' | 'html') || 'text');
      
      console.log('[DocumentsTab] Loaded document format:', contentData.format);
    } catch (err) {
      console.error('Error loading document content:', err);
      setError('Failed to load document content');
      setDocumentContent(null);
      setDocumentContentFormat(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentSelect = async (document: Document) => {
    setSelectedDocument(document);
    await loadDocumentContent(document);
  };

  const toggleEdit = (docId: string) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc || documentStates[docId]?.isLocked) return;
    
    navigate(`/documents/edit/${docId}`);
  };

  const toggleLock = (docId: string) => {
    setDocumentStates(prev => ({
      ...prev,
      [docId]: {
        ...prev[docId],
        isLocked: !prev[docId].isLocked,
        isEditing: prev[docId].isLocked ? false : prev[docId].isEditing
      }
    }));
  };

  const handleSave = async (blob: Blob) => {
    if (!selectedDocument || !token) return;
    
    try {
      console.log('Saving document:', selectedDocument.id, blob);
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document');
    }
  };

  const handleDownload = async (doc: Document) => {
    if (!token) return;
    
    try {
      const response = await apiCall(
        `${ENDPOINTS.DOCUMENTS.LIST}${doc.id}/download/`,
        { method: 'GET' },
        token
      );
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.title || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    }
  };

  const handlePrint = () => {
    if (!selectedDocument || !documentContent) {
      console.log('No document selected or content not loaded');
      return;
    }
    
    // Use Syncfusion's print method for SFDT/DOCX documents
    if (documentContentFormat !== 'html' && editorRef.current?.documentEditor) {
      editorRef.current.documentEditor.print();
    } else {
      // For HTML documents, use window.print()
      setTimeout(() => {
        window.print();
      }, 100);
    }
  };

  const handleCreateDocument = () => {
    if (patient) {
      navigate('/documents/new', { state: { patient: patient, patientId: patient.id } });
    }
  };

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 380px)' }}>
      <style>{`
        @media print {
          /* Hide everything except the document content */
          body * {
            visibility: hidden;
          }
          
          /* Show only the document content */
          .print-content, .print-content * {
            visibility: visible !important;
          }
          
          /* Position document content at top of page */
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white;
          }
          
          /* Remove padding/margins for print */
          .print-content {
            padding: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>
      {/* Action Bar */}
      <div className="bg-base-200 p-4 shadow-lg flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-2">
            <button
              onClick={handleCreateDocument}
              className="btn btn-primary gap-2"
            >
              <FiPlus className="w-4 h-4" />
              Create Document
            </button>
            <button className="btn btn-success gap-2">
              <FiUpload className="w-4 h-4" />
              Upload
            </button>
          </div>
          <div className="text-sm opacity-70">
            {documents.length} document{documents.length !== 1 ? 's' : ''} for {patient?.first_name} {patient?.last_name}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-row overflow-hidden">
        {/* Left sidebar - Document list */}
        <div className="w-80 flex-shrink-0 border-r border-base-300 bg-base-100">
          <div className="flex flex-col h-full">
            <div className="p-4 border-b border-base-300 bg-base-200">
              <h2 className="text-lg font-semibold">Documents</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {documents.length === 0 ? (
                <div className="p-4 text-center opacity-60">
                  <p className="mb-2">No documents found</p>
                  <p className="text-xs opacity-70">
                    This patient has no documents yet
                  </p>
                </div>
              ) : (
                documents.map((doc) => (
                  <DocumentItem
                    key={doc.id}
                    document={doc}
                    isSelected={selectedDocument?.id === doc.id}
                    onClick={() => handleDocumentSelect(doc)}
                    onEdit={() => toggleEdit(doc.id)}
                    onToggleLock={() => toggleLock(doc.id)}
                    onDownload={() => handleDownload(doc)}
                    onPrint={async () => {
                      if (selectedDocument?.id !== doc.id) {
                        await handleDocumentSelect(doc);
                      }
                      handlePrint();
                    }}
                    isLocked={documentStates[doc.id]?.isLocked ?? true}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Document viewer/editor area */}
        <div className="flex-1 overflow-hidden">
          {loading && selectedDocument ? (
            <div className="h-full flex items-center justify-center opacity-60">
              Loading document...
            </div>
          ) : error && selectedDocument ? (
            <div className="h-full flex items-center justify-center text-error">
              {error}
            </div>
          ) : selectedDocument && documentContent ? (
            <div className="relative h-full bg-gray-200 print-content">
              {documentContentFormat === 'html' ? (
                <div className="h-full overflow-auto p-8">
                  <style>{`
                    .medical-letter-print * {
                      color: #000000 !important;
                    }
                  `}</style>
                  <div 
                    className="max-w-[210mm] mx-auto bg-white shadow-lg medical-letter-print"
                    style={{
                      minHeight: '297mm',
                      padding: '25mm 20mm',
                      color: '#000000',
                      fontSize: '11pt',
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: '1.6'
                    }}
                    dangerouslySetInnerHTML={{ __html: documentContent }}
                  />
                </div>
              ) : (
                <DocumentEditor
                  ref={editorRef}
                  initialContent={documentContent}
                  onSave={handleSave}
                  isReadOnly={documentStates[selectedDocument.id]?.isLocked ?? true}
                  enableToolbar={false}
                  showPropertiesPane={false}
                  height="100%"
                />
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center opacity-60">
              Select a document to view
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsTab;
